import { useCallback, useEffect, useRef, useState } from 'react'
import { useGamepad, type StickWerte } from './useGamepad'
import { useStickSocket } from './useStickSocket'

// Handsteuerung (virtuelle Sticks) des Cockpits. Drei Eingabearten laufen parallel, je Achse gilt der größte Ausschlag:
//   Gamepad (Modus 2) · Tastatur (W/S vor/zurück, A/D seitlich, Q/E drehen, C steigen, Z sinken) · Wisch-Sticks auf dem Bild.
// Totmann: Gesendet wird alle 100 ms, solange die Handsteuerung an ist – ohne Ausschlag/Taste immer 0. Sie geht sofort aus
// bei Sperre (Steuerung weg, Drohne gelandet, FlyTo/POI/RTH), Seitenwechsel, Tab verstecken, Fensterfokus weg,
// WebSocket-Fehler und Gamepad-Trennung (der Socket wird dabei geschlossen).
// Der Notstopp (Leertaste, Gamepad B) liegt ebenfalls hier, weil er immer gelten muss, auch bei ausgeschalteter Handsteuerung.

export type Stufe = 1 | 3 | 5
export const STUFEN: Stufe[] = [1, 3, 5]
export const NULL_WERTE: StickWerte = { x: 0, y: 0, z: 0, r: 0 }
const TAKT_MS = 100

/** Tastenbelegung (event.code) -> Achse und Richtung */
const TASTEN: Record<string, [keyof StickWerte, number]> = {
  KeyW: ['y', 1], KeyS: ['y', -1], KeyD: ['x', 1], KeyA: ['x', -1],
  KeyE: ['r', 1], KeyQ: ['r', -1], KeyC: ['z', 1], KeyZ: ['z', -1],
}

export const istEingabefeld = (t: EventTarget | null) => {
  const el = t as HTMLElement | null
  return !!el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable)
}

const groesser = (a: number, b: number) => (Math.abs(b) > Math.abs(a) ? b : a)

export function useHandsteuerung({ sn, sperre, steuerungErteilt, onNotstopp, onMeldung }: {
  sn: string
  /** Grund, warum die Handsteuerung gerade nicht möglich ist (null = möglich) */
  sperre: string | null
  /** Steuerung an diesen Nutzer erteilt (Gamepad-Notstopp ist dann scharf) */
  steuerungErteilt: boolean
  onNotstopp: () => void
  onMeldung: (text: string, fehler: boolean) => void
}) {
  const [an, setAn] = useState(false)
  const [stufe, setStufe] = useState<Stufe>(1)
  const [werte, setWerte] = useState<StickWerte>(NULL_WERTE)
  const [fehler, setFehler] = useState<string | null>(null)
  const anRef = useRef(false)
  const stufeRef = useRef<Stufe>(1)
  stufeRef.current = stufe
  const tasten = useRef(new Set<string>())
  const touch = useRef({ links: { x: 0, y: 0 }, rechts: { x: 0, y: 0 } })
  const schliessenRef = useRef<() => void>(() => {})
  const meldung = useRef(onMeldung)
  meldung.current = onMeldung

  const ausschalten = useCallback((grund?: string, istFehler = true) => {
    const warAn = anRef.current
    anRef.current = false
    tasten.current.clear()
    touch.current = { links: { x: 0, y: 0 }, rechts: { x: 0, y: 0 } }
    setAn(false); setWerte(NULL_WERTE)
    schliessenRef.current()
    if (grund && warAn) { setFehler(istFehler ? grund : null); meldung.current(grund, istFehler) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const socket = useStickSocket(text => ausschalten(text))
  const { oeffnen, senden } = socket
  schliessenRef.current = socket.schliessen
  /** Notstopp: Handsteuerung sofort aus (Sticks 0, Socket zu), dann Befehl an die Drohne */
  const cbNotstopp = useRef(onNotstopp)
  cbNotstopp.current = onNotstopp
  const notstopp = useCallback(() => { ausschalten(); cbNotstopp.current() }, [ausschalten])
  const gamepad = useGamepad({
    aktiv: steuerungErteilt,
    onNotstopp: notstopp,
    onGetrennt: () => { if (anRef.current) ausschalten('Handsteuerung aus: Gamepad getrennt.') },
  })

  const gamepadLesen = gamepad.lesen
  const einschalten = useCallback(() => {
    if (sperre || anRef.current) return
    setFehler(null)
    anRef.current = true
    setAn(true)
    oeffnen(sn)
  }, [sperre, sn, oeffnen])

  // Notstopp per Leertaste: ohne Rückfrage, solange die Steuerung erteilt ist und kein Eingabefeld Text erwartet
  useEffect(() => {
    if (!steuerungErteilt) return
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || istEingabefeld(e.target)) return
      e.preventDefault()
      if (!e.repeat) notstopp()
    }
    const up = (e: KeyboardEvent) => { if (e.code === 'Space' && !istEingabefeld(e.target)) e.preventDefault() }   // kein „Klick“ auf fokussierte Knöpfe
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up) }
  }, [steuerungErteilt, notstopp])

  // Sperre eingetreten (Steuerung zurückgenommen, gelandet, FlyTo/POI/RTH, andere Drohne): sofort aus
  useEffect(() => { if (an && sperre) ausschalten(`Handsteuerung aus: ${sperre}`) }, [an, sperre, ausschalten])
  useEffect(() => () => { anRef.current = false }, [])

  // Sicherheit: Seite/Tab/Fenster verlassen
  useEffect(() => {
    if (!an) return
    const aus = (text: string) => () => ausschalten(text)
    const verstecken = () => { if (document.hidden) ausschalten('Handsteuerung aus: Tab im Hintergrund.') }
    const bei = { blur: aus('Handsteuerung aus: Fenster hat den Fokus verloren.'), pagehide: aus('Handsteuerung aus: Seite wird verlassen.') }
    document.addEventListener('visibilitychange', verstecken)
    window.addEventListener('blur', bei.blur)
    window.addEventListener('pagehide', bei.pagehide)
    return () => {
      document.removeEventListener('visibilitychange', verstecken)
      window.removeEventListener('blur', bei.blur)
      window.removeEventListener('pagehide', bei.pagehide)
    }
  }, [an, ausschalten])

  // Tastatur (nur wenn kein Eingabefeld fokussiert ist); Tasten werden beim Loslassen und bei Fokusverlust vergessen
  useEffect(() => {
    if (!an) return
    const down = (e: KeyboardEvent) => {
      if (!TASTEN[e.code] || istEingabefeld(e.target) || istEingabefeld(document.activeElement) || e.ctrlKey || e.metaKey || e.altKey) return
      e.preventDefault()
      tasten.current.add(e.code)
    }
    const up = (e: KeyboardEvent) => { tasten.current.delete(e.code) }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); tasten.current.clear() }
  }, [an])

  // Sendetakt: Werte aus allen Quellen zusammenführen und senden (auch Nullwerte – das ist der Herzschlag)
  useEffect(() => {
    if (!an) return
    const t = setInterval(() => {
      const w: StickWerte = { ...NULL_WERTE }
      for (const code of tasten.current) { const [achse, richtung] = TASTEN[code]; w[achse] = groesser(w[achse], richtung) }
      const g = gamepadLesen()
      const tl = touch.current.links, tr = touch.current.rechts
      const quellen: StickWerte[] = [g ?? NULL_WERTE, { x: tr.x, y: tr.y, z: tl.y, r: tl.x }]
      for (const q of quellen) for (const k of ['x', 'y', 'z', 'r'] as const) w[k] = groesser(w[k], q[k])
      for (const k of ['x', 'y', 'z', 'r'] as const) w[k] = Math.round(Math.max(-1, Math.min(1, w[k])) * 100) / 100
      senden({ ...w, stufe: stufeRef.current })
      setWerte(alt => (alt.x === w.x && alt.y === w.y && alt.z === w.z && alt.r === w.r ? alt : w))
    }, TAKT_MS)
    return () => clearInterval(t)
  }, [an, gamepadLesen, senden])

  /** Wisch-Sticks melden ihre Auslenkung (-1..1, y nach oben positiv) */
  const touchSetzen = useCallback((seite: 'links' | 'rechts', x: number, y: number) => { touch.current[seite] = { x, y } }, [])

  return { an, einschalten, ausschalten, stufe, setStufe, werte, fehler, gamepad, touchSetzen, notstopp, socketOffen: socket.offen, latenzMs: socket.latenzMs }
}
